import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { account, payments, ApiError, type CreditEntry } from '../lib/api'
import { useMe } from '../lib/useMe'
import { AuthLayout, ErrorBox, OkBox, PageLoading } from '../components/AuthLayout'
import { DataTable, columnHelperFor } from '../components/DataTable'

// Balance, coupon redemption and the mutation log. All of this used to sit on the
// dashboard, above and below the charts, which put the least-consulted numbers in the
// most valuable space: the dashboard is for whether the brand is being mentioned, not
// for bookkeeping. The running balance still shows in the app header, so nothing is
// hidden; it just no longer competes with the stats.
const CREDIT_REASON_LABELS: Record<string, string> = {
  coupon: 'Coupon verzilverd',
  run_hold: 'Credits gereserveerd voor meting',
  run_settle: 'Meting afgerekend',
  refund: 'Terugbetaling',
  payment: 'Credits gekocht',
}

const col = columnHelperFor<CreditEntry>()

const mutationColumns = [
  col.accessor('CreatedAt', {
    id: 'created',
    header: 'Wanneer',
    cell: (info) => new Date(info.getValue()).toLocaleString('nl-NL'),
  }),
  col.accessor('Reason', {
    id: 'reason',
    header: 'Reden',
    cell: (info) => CREDIT_REASON_LABELS[info.getValue()] ?? info.getValue(),
  }),
  col.accessor('Note', {
    id: 'note',
    header: 'Referentie',
    cell: (info) => (
      <span className="break-all font-mono text-xs text-ink-soft">{info.getValue() ?? ''}</span>
    ),
  }),
  col.accessor('Delta', {
    id: 'delta',
    header: 'Aantal',
    cell: (info) => `${info.getValue() > 0 ? '+' : ''}${info.getValue()}`,
  }),
]

export function Credits() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const me = useMe()
  const history = useQuery({ queryKey: ['credits-history'], queryFn: account.creditsHistory, enabled: me.isSuccess })
  const packs = useQuery({ queryKey: ['credit-packs'], queryFn: payments.packs, enabled: me.isSuccess })
  const [searchParams] = useSearchParams()

  const [couponCode, setCouponCode] = useState('')
  const [error, setError] = useState('')
  const [granted, setGranted] = useState<number | null>(null)
  const [testModeStarted, setTestModeStarted] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [buying, setBuying] = useState('')

  // Coming back from a completed checkout. The credits are granted by the provider's
  // webhook, not by this redirect, so the page may briefly still show the old balance:
  // refetch instead of trusting the URL, and say so if it has not landed yet.
  const paid = searchParams.get('betaald') === '1'
  useEffect(() => {
    if (!paid) return
    const timers = [0, 2000, 5000].map((ms) =>
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['me'] })
        queryClient.invalidateQueries({ queryKey: ['credits-history'] })
      }, ms),
    )
    return () => timers.forEach(clearTimeout)
  }, [paid, queryClient])

  const notLoggedIn = me.isError
  useEffect(() => {
    if (notLoggedIn) navigate('/login')
  }, [notLoggedIn, navigate])

  async function onRedeem(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setGranted(null)
    setTestModeStarted(null)
    setBusy(true)
    try {
      const res = await account.redeemCoupon(couponCode.trim())
      // A test-mode coupon grants no credits, so reporting the number would read as a
      // failure for the one redemption that changes what money means here.
      if (res.test_mode_until) {
        setTestModeStarted(res.test_mode_until)
        setGranted(null)
      } else {
        setGranted(res.granted)
      }
      setCouponCode('')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['me'] }),
        queryClient.invalidateQueries({ queryKey: ['credits-history'] }),
        // Without this the mode banner stays hidden until a reload, because
        // refetchOnWindowFocus is off globally: exactly the invisible sandbox this
        // feature exists to avoid.
        queryClient.invalidateQueries({ queryKey: ['credit-packs'] }),
      ])
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon de coupon niet verzilveren.')
    } finally {
      setBusy(false)
    }
  }

  async function onBuy(packID: string) {
    setError('')
    setBuying(packID)
    try {
      const { url } = await payments.checkout(packID)
      window.location.href = url
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon de betaling niet starten.')
      setBuying('')
    }
  }

  if (me.isLoading) return <PageLoading />
  if (me.isError || !me.data) return null

  const balanceEUR = (me.data.credits / 100).toFixed(2)

  return (
    <AuthLayout title="Credits" wide>
      <p className="-mt-4 mb-6 text-sm">
        <Link to="/dashboard" className="text-amber">
          ← Terug naar het dashboard
        </Link>
      </p>
      <ErrorBox>{error}</ErrorBox>
      {granted !== null && <OkBox>{granted} credits toegevoegd.</OkBox>}
      {testModeStarted && (
        <OkBox>
          Testmodus staat aan tot {new Date(testModeStarted).toLocaleString('nl-NL')}. Betalingen
          gaan naar de testomgeving en er wordt geen geld afgeschreven.
        </OkBox>
      )}
      {paid && granted === null && (
        <OkBox>Betaling ontvangen. Je credits staan er binnen een minuut op.</OkBox>
      )}

      <div className="mb-4 rounded-xl border border-line bg-card p-5">
        <div className="text-sm text-ink-soft">Saldo</div>
        <div className="text-4xl font-semibold tracking-tight">{me.data.credits} credits</div>
        <div className="text-sm text-ink-soft">≈ € {balanceEUR}</div>
      </div>

      {packs.data?.mode === 'test' && (
        <div className="mb-4 rounded-xl border border-amber bg-amber/10 p-4 text-sm">
          <strong>Testmodus staat aan.</strong> Betalingen gaan naar de testomgeving: er wordt
          geen geld afgeschreven en alleen testkaarten werken. Dit stopt vanzelf.
        </div>
      )}

      {packs.data?.enabled && (
        <div className="mb-4 rounded-xl border border-line bg-card p-5">
          <div className="mb-3 text-sm text-ink-soft">Credits kopen</div>
          <div className="grid gap-3 sm:grid-cols-3">
            {packs.data.packs.map((pack) => (
              <button
                key={pack.id}
                type="button"
                onClick={() => onBuy(pack.id)}
                disabled={buying !== ''}
                className="rounded-lg border border-line px-4 py-3 text-left hover:border-amber disabled:opacity-50"
              >
                <div className="text-lg font-semibold">{pack.label}</div>
                <div className="text-sm text-ink-soft">
                  {buying === pack.id
                    ? 'Bezig...'
                    : `€ ${(pack.amount_cents / 100).toFixed(2).replace('.', ',')}`}
                </div>
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs text-ink-soft">
            Je rekent af bij onze betaalprovider. Wij slaan geen kaartgegevens op.
          </p>
        </div>
      )}

      <div className="mb-4 rounded-xl border border-line bg-card p-5">
        <div className="mb-2 text-sm text-ink-soft">Coupon verzilveren</div>
        <form onSubmit={onRedeem} className="flex gap-2">
          <input
            type="text"
            required
            placeholder="MT-XXXXXXXX"
            className="w-full rounded-lg border border-line bg-card px-3 py-2.5 uppercase text-ink focus:outline-none focus:ring-2 focus:ring-amber"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
          />
          <button
            type="submit"
            disabled={busy}
            className="rounded-lg bg-amber px-4 py-2.5 font-semibold text-[#0E2A3B] hover:opacity-90 disabled:opacity-50"
          >
            Verzilveren
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-line bg-card p-5">
        <div className="mb-2 text-sm text-ink-soft">Recente mutaties</div>
        <DataTable
          label="mutaties"
          rows={history.data ?? []}
          columns={mutationColumns}
          // The reference is a Stripe session or payment id: long, monospace, and only
          // wanted when someone is reconciling a specific charge. On by default it pushed
          // the numbers off the edge of the row.
          initiallyHidden={['note']}
          emptyLabel="Nog geen mutaties."
        />
      </div>
    </AuthLayout>
  )
}
